import {
  ACCEL_AIR, ACCEL_GROUND, BTN_JUMP, BTN_LEFT, BTN_RIGHT, CAMERA_LEAD, COIN_POINTS, COYOTE_TICKS,
  FALL_MAX, FRICTION_AIR, FRICTION_GROUND, GRAVITY, JUMP_BUFFER_TICKS, JUMP_CUT, JUMP_V, PLAYER_H,
  PLAYER_W, RIVAL_BONUS, ROWS, RUN_MAX, SAB_DURATION, SCROLL_DELAY_TICKS, SCROLL_MAX, SCROLL_MIN,
  SCROLL_RAMP_TICKS, SPEED_BOOST, SPEED_BOOST_CAP, START_GROUND, START_X, SUB, SURVIVE_BONUS, TILE,
  VIEW_W, WORLD_H, type SabKind,
} from './constants.ts';
import { Level, T_COIN, T_SOLID, T_SPIKE } from './level.ts';
import { Rng, hash2 } from './rng.ts';

export interface SabEvent {
  id: number;
  kind: SabKind;
}

export interface Hazard {
  x: number;
  y: number;
}

export const HAZARD_W = 10 * SUB;
export const HAZARD_H = 12 * SUB;
const HAZARD_VY = 40;

export type DeathReason = 'fell' | 'spiked' | 'left behind' | 'rained on';

/**
 * The whole game state. `step(input, events)` advances exactly one 60 Hz tick.
 * Pure integer math, no Math.random / Date / floats-of-doubt, so it can be
 * replayed identically by the server (validation) and spectators (viewing).
 */
export class Sim {
  readonly seed: number;
  readonly level: Level;
  tick = 0;
  x = START_X;
  y = WORLD_H - START_GROUND * TILE - PLAYER_H;
  vx = 0;
  vy = 0;
  onGround = false;
  coyote = 0;
  jumpBuf = 0;
  prevInput = 0;
  scrollX = 0;
  alive = true;
  deathReason: DeathReason | null = null;
  deathTick = -1;
  maxX = START_X;
  coins = 0;
  bonus = 0;
  collected = new Set<number>();
  hazards: Hazard[] = [];
  speedUntil = 0;
  rainUntil = 0;
  darkUntil = 0;
  flipUntil = 0;
  bonusAt: number[] = [];
  /** Distances (m) of the previous players to beat, ascending; `beaten` of them are behind us. */
  readonly rivals: readonly number[];
  beaten = 0;
  lastBeatTick = -1;
  lastEvent: { kind: SabKind; tick: number } | null = null;
  lastBonusTick = -1;

  constructor(seed: number, rivals: readonly number[] = [], level?: Level) {
    this.seed = seed >>> 0;
    this.rivals = rivals.slice().sort((a, b) => a - b);
    this.level = level ?? new Level(this.seed);
  }

  get distance(): number {
    return Math.floor((this.maxX - START_X) / TILE);
  }

  get score(): number {
    return this.distance + this.coins * COIN_POINTS + this.bonus + this.beaten * RIVAL_BONUS;
  }

  scrollSpeed(): number {
    const t = this.tick;
    if (t < SCROLL_DELAY_TICKS) return 0;
    const ramp = Math.min(t - SCROLL_DELAY_TICKS, SCROLL_RAMP_TICKS);
    let s = SCROLL_MIN + Math.floor(((SCROLL_MAX - SCROLL_MIN) * ramp) / SCROLL_RAMP_TICKS);
    if (this.speedUntil > t) s = Math.min(s + SPEED_BOOST, SPEED_BOOST_CAP);
    return s;
  }

  step(input: number, events?: readonly SabEvent[]): void {
    if (!this.alive) return;
    const t = this.tick;
    if (events) for (const ev of events) this.apply(ev);

    let dir = 0;
    if (input & BTN_LEFT) dir -= 1;
    if (input & BTN_RIGHT) dir += 1;
    if (this.flipUntil > t) dir = -dir;

    if (dir !== 0) {
      const a = this.onGround ? ACCEL_GROUND : ACCEL_AIR;
      let vx = this.vx + dir * a;
      if (dir * this.vx < 0) vx += dir * a; // turn around faster
      this.vx = vx > RUN_MAX ? RUN_MAX : vx < -RUN_MAX ? -RUN_MAX : vx;
    } else {
      const f = this.onGround ? FRICTION_GROUND : FRICTION_AIR;
      if (this.vx > 0) this.vx = Math.max(0, this.vx - f);
      else if (this.vx < 0) this.vx = Math.min(0, this.vx + f);
    }

    const jumpHeld = (input & BTN_JUMP) !== 0;
    if (jumpHeld && !(this.prevInput & BTN_JUMP)) this.jumpBuf = JUMP_BUFFER_TICKS;
    if (this.onGround) this.coyote = COYOTE_TICKS;
    if (this.jumpBuf > 0 && this.coyote > 0) {
      this.vy = -JUMP_V;
      this.jumpBuf = 0;
      this.coyote = 0;
      this.onGround = false;
    }
    if (this.jumpBuf > 0) this.jumpBuf--;
    if (!this.onGround && this.coyote > 0) this.coyote--;
    if (!jumpHeld && this.vy < -JUMP_CUT) this.vy = -JUMP_CUT;
    this.vy = Math.min(this.vy + GRAVITY, FALL_MAX);
    this.prevInput = input;

    this.moveX();
    this.moveY();

    this.scrollX += this.scrollSpeed();
    const lead = this.x + (PLAYER_W >> 1) - CAMERA_LEAD;
    if (lead > this.scrollX) this.scrollX = lead;
    if (this.x > this.maxX) this.maxX = this.x;
    while (this.beaten < this.rivals.length && this.distance > this.rivals[this.beaten]) {
      this.beaten++;
      this.lastBeatTick = t;
    }

    this.touchTiles();
    this.updateHazards();
    if (this.alive && this.y > WORLD_H) this.die('fell');
    if (this.alive && this.x + PLAYER_W < this.scrollX) this.die('left behind');

    while (this.bonusAt.length && this.bonusAt[0] <= t) {
      this.bonusAt.shift();
      if (this.alive) {
        this.bonus += SURVIVE_BONUS;
        this.lastBonusTick = t;
      }
    }
    this.tick++;
  }

  private apply(ev: SabEvent): void {
    const t = this.tick;
    const until = t + SAB_DURATION[ev.kind];
    switch (ev.kind) {
      case 'speed':
        this.speedUntil = until;
        break;
      case 'dark':
        this.darkUntil = until;
        break;
      case 'flip':
        this.flipUntil = until;
        break;
      case 'rain': {
        this.rainUntil = until;
        const r = new Rng(hash2(this.seed, t + 1));
        const n = 6 + Math.floor(Math.min(t, 7200) / 2400);
        for (let i = 0; i < n; i++) {
          this.hazards.push({
            x: this.scrollX + VIEW_W / 5 + Math.floor(r.next() * ((VIEW_W * 9) / 10)),
            y: -HAZARD_H - Math.floor(r.next() * WORLD_H),
          });
        }
        break;
      }
    }
    this.bonusAt.push(until);
    this.bonusAt.sort((a, b) => a - b);
    this.lastEvent = { kind: ev.kind, tick: t };
  }

  private solid(c: number, r: number): boolean {
    return this.level.tile(c, r) === T_SOLID;
  }

  private moveX(): void {
    if (this.vx === 0) return;
    this.x += this.vx;
    const r0 = Math.floor(this.y / TILE);
    const r1 = Math.floor((this.y + PLAYER_H - 1) / TILE);
    if (this.vx > 0) {
      const c = Math.floor((this.x + PLAYER_W - 1) / TILE);
      for (let r = r0; r <= r1; r++) {
        if (this.solid(c, r)) {
          this.x = c * TILE - PLAYER_W;
          this.vx = 0;
          return;
        }
      }
    } else {
      const c = Math.floor(this.x / TILE);
      for (let r = r0; r <= r1; r++) {
        if (this.solid(c, r)) {
          this.x = (c + 1) * TILE;
          this.vx = 0;
          return;
        }
      }
    }
  }

  private moveY(): void {
    this.y += this.vy;
    this.onGround = false;
    const c0 = Math.floor(this.x / TILE);
    const c1 = Math.floor((this.x + PLAYER_W - 1) / TILE);
    if (this.vy > 0) {
      const r = Math.floor((this.y + PLAYER_H - 1) / TILE);
      for (let c = c0; c <= c1; c++) {
        if (this.solid(c, r)) {
          this.y = r * TILE - PLAYER_H;
          this.vy = 0;
          this.onGround = true;
          return;
        }
      }
    } else if (this.vy < 0) {
      const r = Math.floor(this.y / TILE);
      for (let c = c0; c <= c1; c++) {
        if (this.solid(c, r)) {
          this.y = (r + 1) * TILE;
          this.vy = 0;
          return;
        }
      }
    }
  }

  private touchTiles(): void {
    const c0 = Math.floor(this.x / TILE);
    const c1 = Math.floor((this.x + PLAYER_W - 1) / TILE);
    const r0 = Math.max(0, Math.floor(this.y / TILE));
    const r1 = Math.min(ROWS - 1, Math.floor((this.y + PLAYER_H - 1) / TILE));
    for (let c = c0; c <= c1; c++) {
      for (let r = r0; r <= r1; r++) {
        const t = this.level.tile(c, r);
        if (t === T_SPIKE) {
          if (overlap(this.x, this.y, PLAYER_W, PLAYER_H, c * TILE + 3 * SUB, r * TILE + 7 * SUB, 10 * SUB, 9 * SUB)) {
            this.die('spiked');
            return;
          }
        } else if (t === T_COIN) {
          const key = c * ROWS + r;
          if (!this.collected.has(key)) {
            this.collected.add(key);
            this.coins++;
          }
        }
      }
    }
  }

  private updateHazards(): void {
    if (this.hazards.length === 0) return;
    const keep: Hazard[] = [];
    for (const h of this.hazards) {
      h.y += HAZARD_VY;
      if (h.y > WORLD_H) continue;
      keep.push(h);
      if (this.alive && overlap(this.x + SUB, this.y + SUB, PLAYER_W - 2 * SUB, PLAYER_H - SUB, h.x, h.y, HAZARD_W, HAZARD_H)) {
        this.die('rained on');
      }
    }
    this.hazards = keep;
  }

  private die(reason: DeathReason): void {
    this.alive = false;
    this.deathReason = reason;
    this.deathTick = this.tick;
  }

  /** Cheap copy for look-ahead (bots). Shares the Level, which is append-only and deterministic. */
  clone(): Sim {
    const s = Object.create(Sim.prototype) as Sim;
    Object.assign(s, this);
    s.hazards = this.hazards.map((h) => ({ x: h.x, y: h.y }));
    s.collected = new Set(this.collected);
    s.bonusAt = this.bonusAt.slice();
    return s;
  }

  /** Compact fingerprint of the state, for desync tests/diagnostics. */
  fingerprint(): string {
    return [this.tick, this.x, this.y, this.vx, this.vy, this.scrollX, this.coins, this.bonus, this.beaten, this.alive ? 1 : 0, this.hazards.length].join(',');
  }
}

function overlap(ax: number, ay: number, aw: number, ah: number, bx: number, by: number, bw: number, bh: number): boolean {
  return ax < bx + bw && bx < ax + aw && ay < by + bh && by < ay + ah;
}
